/* eslint-disable max-classes-per-file */

import isArray from 'lodash/isArray';
import isNaN from 'lodash/isNaN';
import toNumber from 'lodash/toNumber';
import isUndefined from 'lodash/isUndefined';
import shortid from 'shortid';
import toString from '../utils/toString';
import AbstractControl from './AbstractControl';
import toDataPath from '../utils/toDataPath';
import stringToBoolean from '../utils/stringToBoolean';
import timeRegExp from '../utils/timeRegExp';
import getPointer from '../utils/getPointer';
import removeKeyFromObj from '../utils/removeKeyFromObj';
import combineProps from '../utils/combineProps';
import computeUIProps from '../utils/computeUIProps';
import cleanProps from '../utils/cleanProps';

const getType = (type) => {
  if (Array.isArray(type)) {
    return type[0];
  }

  return type;
};

class TimezoneOffset {
  constructor() {
    this.timezoneOffset = new Date().getTimezoneOffset();
  }

  get value() {
    return this.timezoneOffset;
  }

  get hours() {
    return -(this.timezoneOffset / 60);
  }

  addHours(date) {
    const newDate = new Date(date);
    newDate.setHours(date.getHours() + this.hours);

    return newDate;
  }

  convert(date) {
    const convertedDate = this.addHours(date, this.hours).toJSON().slice(0, -1);

    return `${convertedDate}${this.toString()}`;
  }

  toString() {
    const timezoneIndicator = this.timezoneOffset <= 0 ? '+' : '-';
    return `${timezoneIndicator}${String(`00${Math.abs(-(this.timezoneOffset / 60))}`).slice(
      -2
    )}:00`;
  }
}

const hasSeconds = (date) => {
  if (timeRegExp.test(date)) {
    const timeParts = date.match(timeRegExp);
    return timeParts[3] != null;
  }

  return false;
};

export default class FormFieldControl extends AbstractControl {
  constructor(props) {
    super(props);

    this.id = shortid.generate();

    this.initSubscriptions([
      'aftervalidate',
      'beforevalidate',
      'blur',
      'change',
      'focus',
      'invalid',
      'schema',
      'touched',
      'update',
      'valid',
      'validate',
      'register',
      'unregister',
      'submit',
    ]);

    this.actions = {
      getDefaultHandler: () => this.defaultHandler,
      getErrorMessage: this.getErrorMessage,
      handleBlur: this.handleOnBlur,
      handleChange: this.handleOnChange,
      removeError: this.removeError,
      removeValue: this.removeValue,
      reset: this.reset,
      setError: this.setError,
      setTouched: this.setTouched,
      setValue: this.setValue,
    };
  }

  withContext(context, $ref) {
    super.withContext(context);

    if (this.$ref !== $ref) {
      this.$ref = $ref;
      this.dataPath = toDataPath($ref);
      this.name = this.dataPath === '' ? '_root' : this.dataPath;
    }

    if (context != null) {
      this.field = this.getRefFromSchema();

      const { getError, touched } = context;
      const {
        control,
        defaultValue,
        disabled,
        format,
        id,
        initialValue,
        inputProps,
        isMultistepField,
        keepState,
        max,
        maxLength,
        min,
        minLength,
        pattern,
        relevant,
        required,
        schema,
        type,
        validate,
        validateOnChange,
        ...UIProps
      } = combineProps(this.props, this.field['ui:props']);

      this.userSchema = schema;
      this.keepState = keepState;
      this.UIProps = computeUIProps(UIProps);
      this.UIInputProps = { ...this.field['ui:inputProps'], ...inputProps };
      this.id = id ?? this.field['ui:id'] ?? this.id;
      this.primitiveType = getType(this.field.type ?? this.props?.type) ?? 'string';
      this.options = this.computeOptions();
      this.value = this.computeValue();
      this.format = this.computeFormat(format);
      this.type = this.computeType(type ?? this.primitiveType);
      this.title = this.i18n(this.dataPath, 'title', this.field.title);
      this.description = this.i18n(this.dataPath, 'description', this.field.description);
      this.defaultValue = this.computeDefaultValue();
      this.trueLabel = this.computeTrueLabel();
      this.trueValue = this.computeTrueValue();
      this.falseLabel = this.computeFalseLabel();
      this.falseValue = this.computeFalseValue();
      this.error = getError(this.dataPath);
      this.control = control ?? this.field['ui:control'] ?? this.primitiveType;
      this.disabled = disabled ?? this.field['ui:disabled'];
      this.isTouched = touched.has(this.dataPath);
      this.max = max ?? this.field.maximum ?? this.field.formatMaximum;
      this.maxLength = maxLength ?? this.field.maxLength;
      this.min = min ?? this.field.minimum ?? this.field.formatMinimum;
      this.minLength = minLength ?? this.field.minLength;
      this.initialValue = this.computeInitialValue();
      this.pattern = pattern ?? this.field.pattern;
      this.validateOnChange = validateOnChange;
      this.schemaValidate = validate ?? this.field['ui:validate'];
      this.relevant = relevant ?? this.field['ui:relevant'];
      this.required = required ?? this.field['ui:required'];
      this.userDefaultValue = defaultValue;
      this.userInitialValue = initialValue;
      this.isMultistepField = isMultistepField;
    }

    return this;
  }

  getDefaultReturnValue() {
    if (this.primitiveType === 'array') {
      return [];
    }

    if (this.primitiveType === 'object') {
      return {};
    }

    return '';
  }

  computeDefaultValue() {
    const { getDefaultValue } = this.context;
    const defaultValue = getDefaultValue(this.dataPath);

    if (!isUndefined(defaultValue)) {
      return defaultValue;
    }

    return undefined;
  }

  computeInitialValue() {
    const { getInitialValue } = this.context;
    const initialValue = getInitialValue(this.dataPath);

    if (!isUndefined(initialValue)) {
      return initialValue;
    }

    return undefined;
  }

  computeValue() {
    const { getValue } = this.context;
    let value = getValue(this.dataPath);

    if (isUndefined(value)) {
      value = this.defaultValue;
    }

    // set a sensible default value
    const returnValue = value ?? this.getDefaultReturnValue();
    const numberValue = toString(returnValue).length > 0 ? toNumber(returnValue) : NaN;

    // coerce number value
    if (this.primitiveType === 'number' && !isNaN(numberValue)) {
      return numberValue;
    }

    // format date as HTML5 compliant string
    // only use if actual input element is of type datetime-local
    const inputElement = !isUndefined(document) ? document.getElementById(this.id) : null;
    if (inputElement?.type === 'datetime-local') {
      if (!returnValue) {
        return '';
      }

      const date = new Date(returnValue);

      const yyyy = String(`0000${date.getFullYear()}`).slice(-4);
      const MM = String(`00${date.getMonth() + 1}`).slice(-2);
      const dd = String(`00${date.getDate()}`).slice(-2);
      const hh = String(`00${date.getHours()}`).slice(-2);
      const mm = String(`00${date.getMinutes()}`).slice(-2);
      const ss = String(`00${date.getSeconds()}`).slice(-2);

      const valueHasSeconds = this.prevTargetValue
        ? hasSeconds(this.prevTargetValue)
        : hasSeconds(returnValue);

      return `${yyyy}-${MM}-${dd}T${hh}:${mm}${valueHasSeconds ? `:${ss}` : ''}`;
    }

    // format time as HTML5 compliant string
    if (this.type === 'time' && timeRegExp.test(returnValue)) {
      const timeParts = returnValue.match(timeRegExp);
      const hh = String(`00${timeParts[1]}`).slice(-2);
      const mm = String(`00${timeParts[2]}`).slice(-2);
      const ss = String(`00${timeParts[3]}`).slice(-2);

      const valueHasSeconds = this.prevTargetValue
        ? hasSeconds(this.prevTargetValue)
        : hasSeconds(returnValue);

      return valueHasSeconds ? `${hh}:${mm}:${ss}` : `${hh}:${mm}`;
    }

    return returnValue;
  }

  computeOptions() {
    const { options } = this.props;

    if (Array.isArray(options)) {
      return options;
    }

    const enumerable = this.field.enum;
    const itemsOneOf = this.field.items?.oneOf;
    const oneOf = this.field.oneOf ?? itemsOneOf;
    const i18n = this.i18n(this.dataPath, 'options', {});

    if (Array.isArray(enumerable)) {
      return enumerable.map((value) => ({
        value,
        label: toString(i18n[value] ?? value),
        id: `${this.id}[${JSON.stringify(value)}]`,
      }));
    }

    if (Array.isArray(oneOf)) {
      return oneOf.map(
        ({
          description,
          const: value,
          title: label,
          'ui:disabled': disabled = false,
          'ui:id': id = `${this.id}[${JSON.stringify(value)}]`,
        }) => ({
          description,
          disabled,
          id,
          label: toString(i18n[value] ?? label ?? value),
          value,
        })
      );
    }

    return null;
  }

  computeType(type) {
    // map common types
    return (
      {
        boolean: 'checkbox',
        color: 'color',
        date: 'date',
        'date-time': 'datetime-local',
        email: 'email',
        integer: 'number',
        month: 'month',
        number: 'number',
        password: 'password',
        string: 'text',
        tel: 'tel',
        time: 'time',
        url: 'url',
        week: 'week',
      }[(type === 'string' && this.format) || type] ||
      type ||
      'text'
    ).toLowerCase();
  }

  computeTrueLabel() {
    const { oneOf } = this.field;

    if (isArray(oneOf)) {
      return oneOf.find((valueObj) => valueObj['ui:checked'] === true)?.title ?? null;
    }

    return null;
  }

  computeTrueValue() {
    let trueValue = true;
    const { const: constant, oneOf } = this.field;

    if (isArray(oneOf)) {
      trueValue = oneOf.find((valueObj) => valueObj['ui:checked'] === true)?.const;
    }

    return !isUndefined(constant) ? constant : trueValue;
  }

  computeFalseLabel() {
    const { oneOf } = this.field;

    if (isArray(oneOf)) {
      return oneOf.find((valueObj) => valueObj['ui:checked'] === false)?.title ?? null;
    }

    return null;
  }

  computeFalseValue() {
    let falseValue = false;
    const { oneOf } = this.field;

    if (isArray(oneOf)) {
      falseValue = oneOf.find((valueObj) => valueObj['ui:checked'] === false)?.const;
    }

    return falseValue;
  }

  computeFormat(format) {
    const { format: fieldFormat } = this.field;
    const computedFormat = format ?? fieldFormat;

    return typeof computedFormat === 'string' ? computedFormat.toLowerCase() : undefined;
  }

  getProps = () =>
    cleanProps({
      id: this.id,
      label: this.title,
      options: this.options,
      ...this.UIProps,
      inputProps: {
        format: this.format,
        label: this.title,
        max: this.max,
        maxLength: this.maxLength,
        min: this.min,
        name: this.name,
        options: this.options,
        placeholder: this.description,
        value: this.value,
        ...this.UIInputProps,
        ...this.defaultHandler,
      },
    });

  getUserProps = () =>
    cleanProps({
      label: this.title, // can be overwritten
      ...this.UIProps,
      ...this.UIInputProps,
      // put back some allowed values that were taken out before
      disabled: this.props.disabled,
      max: this.props.max,
      maxLength: this.props.maxLength,
      min: this.props.min,
      minLength: this.props.minLength,
      pattern: this.props.pattern,
      required: this.props.required,
    });

  get properties() {
    const isPristine = this.initialValue === this.value;
    const isValid = this.error == null;

    // By default on blur otherwise on change if prop was passed
    const showError = this.validateOnChange ? !isPristine && !isValid : this.isTouched && !isValid;

    return {
      $dirty: !isPristine,
      $invalid: !isValid,
      $pristine: isPristine,
      $ref: this.$ref,
      $touched: this.isTouched,
      $untouched: !this.isTouched,
      $valid: isValid,
      $showError: showError,
      control: this.control,
      dataPath: this.dataPath,
      defaultHandler: this.defaultHandler,
      defaultValue: this.defaultValue,
      error: this.error,
      falseLabel: this.falseLabel,
      falseValue: this.falseValue,
      field: this.field,
      initialValue: this.initialValue,
      trueLabel: this.trueLabel,
      trueValue: this.trueValue,
      type: this.type,
      value: this.value,
    };
  }

  get defaultHandler() {
    return {
      disabled: this.disabled,
      id: this.id,
      name: this.name,
      onBlur: this.handleOnBlur,
      onChange: this.handleOnChange,
      onFocus: this.handleOnFocus,
      required: Boolean(this.required),
      type: this.UIInputProps?.type || this.type,
      value: this.value,
    };
  }

  updateUserSchema = (schema) => {
    this.context.updateUserSchema(this.$ref, schema);
  };

  updateDefaultValue = (defaultValue) => {
    this.context.updateDefaultValue(this.dataPath, defaultValue);
  };

  updateInitialValue = (initialValue) => {
    this.context.updateInitialValue(this.dataPath, initialValue);
  };

  subscribeContext = () => {
    const { subscribe } = this.context;

    subscribe('aftervalidate', this.handleAfterValidateEvent);
    subscribe('aftervalidate', this.handleValidateEvent);
    subscribe('beforevalidate', this.handleBeforeValidateEvent);
    subscribe('change', this.handleChangeEvent);
    subscribe('error', this.handleErrorChangeEvent);
    subscribe('schema', this.handleSchemaEvent);
    subscribe('touched', this.handleTouchedEvent);
    subscribe('register', this.handleRegisterEvent);
    subscribe('unregister', this.handleUnregisterEvent);
  };

  unsubscribeContext = () => {
    const { unsubscribe } = this.context;

    unsubscribe('aftervalidate', this.handleAfterValidateEvent);
    unsubscribe('aftervalidate', this.handleValidateEvent);
    unsubscribe('beforevalidate', this.handleBeforeValidateEvent);
    unsubscribe('change', this.handleChangeEvent);
    unsubscribe('error', this.handleErrorChangeEvent);
    unsubscribe('schema', this.handleSchemaEvent);
    unsubscribe('touched', this.handleTouchedEvent);
    unsubscribe('register', this.handleRegisterEvent);
    unsubscribe('unregister', this.handleUnregisterEvent);
  };

  register() {
    const { register } = this.context;

    register(this.dataPath, {
      ...this.props,
      $ref: this.$ref,
      defaultValue: this.userDefaultValue,
      format: this.format,
      formFieldControl: this,
      initialValue: this.userInitialValue,
      isMultistepField: this.isMultistepField,
      keepState: this.keepState,
      schema: this.userSchema,
      type: this.primitiveType,
    });
  }

  unregister() {
    const { unregister } = this.context;

    unregister(this.dataPath, {
      $ref: this.$ref,
      keepState: this.keepState,
    });
  }

  handleChangeEvent = (changedValues) => {
    // console.log('CHANGED', changedValues);
    if (Object.keys(changedValues).includes(this.dataPath)) {
      this.trigger('change', this.ownContext);
    }
  };

  handleErrorChangeEvent = (changedErrors) => {
    if (Object.keys(changedErrors).includes(this.dataPath)) {
      const { error } = this.properties;
      const errorHandler = error ? 'invalid' : 'valid';

      this.trigger(errorHandler, this.ownContext);
    }
  };

  handleAfterValidateEvent = (data, errors) =>
    this.triggerAsync('aftervalidate', {
      ...this.ownContext,
      error: errors[this.dataPath],
      value: getPointer(data, this.dataPath),
      values: data,
    });

  handleBeforeValidateEvent = (data) =>
    this.triggerAsync('beforevalidate', {
      ...this.ownContext,
      value: getPointer(data, this.dataPath),
      values: data,
    });

  handleSchemaEvent = (changedSchemaMap) => {
    if (Object.keys(changedSchemaMap).includes(this.$ref)) {
      this.trigger('schema', this.ownContext);
    }
  };

  handleTouchedEvent = (changedTouched) => {
    if (changedTouched.includes(this.dataPath)) {
      this.trigger('touched', this.ownContext);
    }
  };

  handleValidateEvent = async (data, errors) => {
    const value = getPointer(data, this.dataPath);
    const errorMessages = await this.triggerAsync(
      'validate',
      value,
      this.context.formStateRef.current
    );

    const errorMessage = errorMessages.reduce((message, nextMessage) => {
      if (nextMessage != null) {
        return /^\$[\w\\-]+$/.test(nextMessage)
          ? this.getErrorMessage(nextMessage.replace('$', '')) ?? ''
          : nextMessage;
      }

      return message;
    }, errors[this.dataPath]);

    if (typeof errorMessage === 'string') {
      return [
        {
          errors: {
            [this.dataPath]: errorMessage,
          },
        },
      ];
    }

    return [];
  };

  handleRegisterEvent = (dataPath) => {
    if (dataPath === this.dataPath) {
      this.trigger('register', this.ownContext);
    }
  };

  handleUnregisterEvent = (dataPath) => {
    if (dataPath === this.dataPath) {
      this.trigger('unregister', this.ownContext);
    }
  };

  handleOnChange = (arg) => {
    // assume arg is of type event when it has a property 'target'
    if (Object.prototype.hasOwnProperty.call(arg, 'target')) {
      this.prevTargetValue = arg.target.value;

      // set/unset the value in the array
      // if (this.options != null && this.primitiveType === 'array') {
      if (this.primitiveType === 'array') {
        let newValue;

        const array = Array.isArray(this.value) ? this.value : [];

        if (arg.target.multiple) {
          newValue = Array.from(arg.target.options)
            .filter(({ selected }) => !!selected)
            .map(({ value }) => value);
        } else if (arg.target.checked) {
          newValue = array.concat([arg.target.value]);
        } else {
          newValue = array.filter((v) => toString(v) !== toString(arg.target.value));
        }

        this.setValue(newValue);
      }
      // set the correct value for checkboxes
      else if (arg.target.type.toLowerCase() === 'checkbox') {
        let newValue;

        if (arg.target.checked) {
          newValue = this.trueValue;
        } else {
          newValue = this.falseValue;
        }

        this.setTouched();
        this.setValue(newValue);
      }
      // when saving local datetime make sure it includes the current timezone
      else if (arg.target.type.toLowerCase() === 'datetime-local') {
        const date = arg.target.value
          ? new TimezoneOffset().convert(new Date(arg.target.value))
          : void 0; // eslint-disable-line no-void

        this.setValue(date);
      }
      // when saving a `time` value make sure to include seconds and the current timezone
      else if (arg.target.type.toLowerCase() === 'time') {
        const timeParts = arg.target.value.match(timeRegExp);

        const hh = String(`00${timeParts[1] ?? ''}`).slice(-2);
        const mm = String(`00${timeParts[2] ?? ''}`).slice(-2);
        const ss = String(`00${timeParts[3] ?? ''}`).slice(-2);
        const timezoneOffset = new TimezoneOffset().toString();

        const time = `${hh}:${mm}:${ss}${timezoneOffset}`;

        this.setValue(time);
      } else {
        this.setValue(arg.target.value);
      }
    } else {
      this.setValue(arg);
    }
  };

  handleOnBlur = (e) => {
    e.persist();

    if (e.target.type.toLowerCase() !== 'checkbox') {
      this.setTouched();
    }

    const blurContext = {
      ...this.ownContext,
      event: e,
    };

    this.trigger('blur', blurContext);
  };

  handleOnFocus = (e) => {
    e.persist();

    const focusContext = {
      ...this.ownContext,
      event: e,
    };

    this.trigger('focus', focusContext);
  };

  setTouched = (isTouched) => {
    this.context.setTouched(this.dataPath, isTouched);
  };

  setValue = (value) => {
    let newValue = value;

    // coerce boolean fields
    if (this.primitiveType === 'boolean') {
      newValue = stringToBoolean(value);
    }

    // coerce number fields
    if (this.primitiveType === 'number' || this.primitiveType === 'integer') {
      newValue = value !== '' ? toNumber(value) : null;
    }

    // coerce enum fields
    if (this.options != null && Array.isArray(value)) {
      newValue = value.map((arrayValue) => {
        const { value: coercedValue } = this.options.find(
          ({ value: enumValue }) => toString(enumValue) === toString(arrayValue)
        );

        return coercedValue;
      });
    }

    this.context.setValues((values) => ({
      ...values,
      [this.dataPath]: newValue,
    }));
  };

  removeValue = () => {
    this.context.setValues((values) => removeKeyFromObj(values, this.dataPath));
  };

  clearValues = () => {
    this.context.setValues(() => null);
  };

  setRelevance = (relevant) => this.context.setFieldRelevance(this.dataPath, relevant);

  setRelevanceCallback = (callback) => this.context.setRelevanceCallback(this.dataPath, callback);

  setError = (message) => {
    this.context.setErrors((errors) => ({
      ...errors,
      [this.dataPath]: message,
    }));
  };

  removeError = () => {
    this.context.setErrors((errors) => removeKeyFromObj(errors, this.dataPath));
  };

  registerFieldRef = (ref) => {
    this.context.registerFieldRef(this.dataPath, ref);
  };

  isRelevant = () => this.context.isFieldRelevant(this.dataPath);

  reset = () => this.context.resetField(this.dataPath);
}
